<template>
    <PageSidebar>
        <template #sidebarTitle> {{ $t("legal.menu.layout_content") }} </template>
        <template #sidebarMain>
            <div class="p-6 space-y-5">
                <div class="space-y-2">
                    <FormLabel>{{ $t("legal.content.layout") }}</FormLabel>
                    <FormSwitch v-model="layoutType" :options="layoutTypes" />
                </div>
                <FormToggleSm
                    :title="$t('legal.content.display_categories')"
                    :help="$t('legal.content.display_categories_info')"
                    v-model="footer.withCategories"
                />
                <FormVariants
                    :title="$t('legal.content.aligment')"
                    :variants="textAligns"
                    v-model="footer.variant"
                />
                <FormInputPreText
                    id="footerCopyrights"
                    :title="$t('legal.content.rights_reserved_copy')"
                    :placeholder="$t('legal.content.company_name')"
                    :prefix="`© ${new Date().getFullYear()}`"
                    v-model="footer.copyrights"
                />
            </div>

            <CardToggle :title="$t('legal.content.legal_links')">
                <div class="space-y-3">
                    <CardLink
                        :item="legalLinks[0]"
                        :modelValue="!footer.hideTerms"
                        @update:modelValue="footer.hideTerms = !$event"
                    />
                    <CardLink
                        :item="legalLinks[1]"
                        :modelValue="!footer.hidePolicy"
                        @update:modelValue="footer.hidePolicy = !$event"
                    />
                    <CardLink
                        :item="legalLinks[2]"
                        :modelValue="!footer.hideAbout"
                        @update:modelValue="footer.hideAbout = !$event"
                    />
                </div>
            </CardToggle>
            <CardToggle :title="$t('legal.content.newsletter_form')">
                <div class="space-y-5">
                    <FormToggleSm
                        :title="$t('legal.content.show_newsletter_form')"
                        v-model="footer.newsletter"
                    />
                    <template v-if="footer.newsletter">
                        <FormInput
                            :title="$t('legal.content.webhook_url')"
                            :placeholder="'www.example.com'"
                            :help="$t('legal.content.webhook_url_info')"
                            v-model="footer.newsletterUrl"
                        />
                        <FormInput
                            id="footerNewsletterText1"
                            :title="$t('legal.content.newsletter_title')"
                            v-model="footer.newsletterText1"
                        />
                        <FormInput
                            id="footerNewsletterText2"
                            :title="$t('legal.content.newsletter_highlight_title')"
                            v-model="footer.newsletterText2"
                        />
                        <FormInput
                            id="footerNewsletterText3"
                            :title="$t('legal.content.newsletter_paragraph')"
                            v-model="footer.newsletterText3"
                        />
                        <FormInput
                            id="footerNewsletterCta"
                            :title="$t('legal.content.newsletter_button')"
                            v-model="footer.newsletterCta"
                        />

                        <FormVariants
                            :title="$t('legal.content.newsletter_layout')"
                            :variants="newsletterLayouts"
                            v-model="footer.newsletterLayout"
                        />
                    </template>
                </div>
            </CardToggle>
            <CardToggle :title="$t('legal.content.contact_details')">
                <div class="space-y-5">
                    <FormToggleSm
                        :title="$t('legal.content.show_contact_details')"
                        v-model="footer.contactInfo"
                    />
                    <template v-if="footer.contactInfo">
                        <FormVariants
                            :title="$t('legal.content.layout')"
                            :variants="contactLayouts"
                            v-model="footer.contactLayout"
                        />
                    </template>
                </div>
            </CardToggle>
        </template>

        <template #sidebarFooter>
            <cSaveButton @save="saveSnapshot" :disabled="!hasChanges" />
        </template>
    </PageSidebar>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { PageSidebar } from "@easy/ui/page-components";
import { CardToggle, CardLink } from "@easy/ui/cards";
import {
    FormLabel,
    FormSwitch,
    FormToggleSm,
    FormInput,
    FormVariants,
    FormInputPreText,
} from "@easy/ui/forms";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import { useFocusOnQueryField } from "@/shared/utils/forms/useFocusOnQueryField";

const LAYOUT_TYPE = {
    minimalistic: "minimalistic",
    full: "full",
};

export default {
    components: {
        PageSidebar,
        FormLabel,
        FormSwitch,
        FormToggleSm,
        FormInput,
        CardLink,
        FormVariants,
        CardToggle,
        cSaveButton,
        FormInputPreText,
    },
    setup() {
        useFocusOnQueryField();
    },
    computed: {
        ...mapState("legal", ["footer", "hasChanges"]),
        layoutType: {
            get() {
                return this.footer.simple ? LAYOUT_TYPE.minimalistic : "full";
            },
            set(value) {
                if (value === LAYOUT_TYPE.minimalistic) {
                    this.footer.simple = true;
                } else {
                    this.footer.simple = false;
                }
            },
        },
        textAligns() {
            return [
                { value: "left", label: this.$t("legal.content.align_left") },
                { value: "center", label: this.$t("legal.content.align_center") },
                { value: "below", label: this.$t("legal.content.align_bottom") },
            ];
        },
        layoutTypes() {
            return [
                { label: this.$t("legal.content.full"), value: LAYOUT_TYPE.full },
                {
                    label: this.$t("legal.content.minimalistic"),
                    value: LAYOUT_TYPE.minimalistic,
                },
            ];
        },
        legalLinks() {
            return [
                { title: this.$t("legal.menu.terms") },
                { title: this.$t("legal.menu.privacy") },
                { title: this.$t("legal.menu.about") },
            ];
        },
        newsletterLayouts() {
            return [
                { value: "maxi", label: this.$t("legal.content.newsletter_layout_maxi") },
                { value: "midi", label: this.$t("legal.content.newsletter_layout_midi") },
                { value: "mini", label: this.$t("legal.content.newsletter_layout_mini") },
            ];
        },
        contactLayouts() {
            return [
                { value: "col", label: this.$t("legal.content.contact_layout_col") },
                { value: "full", label: this.$t("legal.content.contact_layout_full") },
                { value: "row", label: this.$t("legal.content.contact_layout_row") },
            ];
        },
    },
    methods: {
        ...mapActions("legal/projects", ["saveSnapshot"]),
    },
};
</script>
